/**
 * Integrytas Group — Refresh registry
 *
 * A tiny context that lets the shell's single Refresh control (in the tab strip)
 * reload whichever page is active, without every page hand-rolling its own
 * refresh button. A page registers its reload function keyed by page code; the
 * shell reads the active page's reloader and drives it from the strip button.
 *
 * The registered wrapper always calls the LATEST reload (via a ref), so pages
 * need not memoise their reload to avoid re-register churn. Outside a shell
 * (mobile, tests) the registry is a safe no-op.
 */
import { createContext, useContext, useEffect, useRef } from 'react';

export interface RefreshRegistry {
  /** Register the active reload for a page; returns an unregister cleanup. */
  register: (pageCode: string, reload: () => void) => () => void;
}

export const RefreshRegistryContext = createContext<RefreshRegistry | null>(null);

export function useRefreshRegistry(): RefreshRegistry {
  return useContext(RefreshRegistryContext) ?? NOOP_REGISTRY;
}

const NOOP_REGISTRY: RefreshRegistry = {
  register: () => () => { /* no shell present: nothing to unregister */ },
};

/**
 * Register a page's reload with the shell's Refresh control. Call once per page,
 * passing the page's code and its reload function. The strip's Refresh button
 * then reloads this page while it is the active tab.
 */
export function usePageRefresh(pageCode: string, reload: () => void): void {
  const { register } = useRefreshRegistry();
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => register(pageCode, () => reloadRef.current()), [pageCode, register]);
}
